import type { Controls } from '../core/device';
import { clamp } from '../core/math';
import type { Random } from '../core/Random';
import { banner, roundRect } from './draw';
import type { MiniGame, MiniInput } from './types';

export type BullKind = 'mechanical' | 'rodeo';

/** Eight seconds is a qualified ride. */
export const RIDE_SECONDS = 8;
const LEFT = ['ArrowLeft', 'KeyA'];
const RIGHT = ['ArrowRight', 'KeyD'];

/**
 * Bull riding: the bull bucks and spins and you lean against it. Tap left or right
 * (keys, or either half of the screen) to throw your weight; lean too far and you're
 * eating dirt. Hang on for eight seconds. The rodeo bull is meaner than the one at
 * the bar, and both get meaner the longer you stay on. A good buzz doesn't help.
 */
export class BullRideGame implements MiniGame {
  readonly title: string;
  readonly width = 360;
  readonly height = 240;
  /** Your lean: -1 and 1 are the ground. */
  lean = 0;
  vel = 0;
  t = 0;
  thrown = false;
  private overT = -1;
  private buckIn = 0.6;
  /** The current buck's shove (signed), for shaking the camera. */
  buck = 0;
  /** Nose up/down and the spin, for whoever draws the bull. */
  bullPitch = 0;
  bullYaw = 0;
  /** How worked up the bull is (grows the longer you stay on). */
  anger = 1;
  private readonly mean: number;

  constructor(
    private readonly rng: Random,
    readonly kind: BullKind,
    buzz = 0,
  ) {
    this.title = kind === 'rodeo' ? 'Ride "Widowmaker" (8 seconds)' : 'The Mechanical Bull';
    this.mean = (kind === 'rodeo' ? 1.35 : 0.9) * (1 + clamp((buzz - 40) / 60, 0, 1) * 0.35);
  }

  get over(): boolean {
    return this.overT >= 0;
  }

  get done(): boolean {
    return this.over && this.t - this.overT > 1.4;
  }

  /** Seconds you stayed on. */
  get time(): number {
    return Math.min(RIDE_SECONDS, this.over ? this.overT : this.t);
  }

  get qualified(): boolean {
    return this.over && !this.thrown;
  }

  hint(controls: Controls): string {
    const lean = {
      keys: 'Tap ←/→ (or A/D)',
      touch: 'Tap the LEFT or RIGHT side',
      pad: 'Flick the stick (or D-pad) ←/→',
    }[controls];
    return `${lean} to lean against the bull. Hang on 8 seconds!`;
  }

  update(dt: number, input: MiniInput): void {
    this.t += dt;
    if (this.over) {
      if (this.thrown) this.lean = clamp(this.lean + Math.sign(this.lean || 1) * dt * 3, -1.6, 1.6);
      return;
    }
    // Lean against it.
    let push = 0;
    if (input.keys.some((k) => LEFT.includes(k))) push -= 1;
    if (input.keys.some((k) => RIGHT.includes(k))) push += 1;
    if (input.tap) push += input.tap.x < this.width / 2 ? -1 : 1;
    this.vel += push * 1.45;

    // The bull: bucks come faster and harder the longer you last.
    const anger = this.mean * (1 + this.t / RIDE_SECONDS);
    this.anger = anger;
    this.buckIn -= dt;
    if (this.buckIn <= 0) {
      this.buckIn = this.rng.range(0.45, 0.95) / Math.sqrt(anger);
      this.buck = (this.rng.chance(0.5) ? 1 : -1) * this.rng.range(0.9, 1.5) * anger;
      this.bullPitch = this.rng.range(0.25, 0.5) * Math.sign(this.rng.next() - 0.5);
    }
    this.vel += this.buck * dt * 2.4;
    this.buck *= Math.exp(-dt * 6);
    // You're top-heavy: once you lean, gravity wants more.
    this.vel += this.lean * 2.2 * dt;
    this.vel *= Math.exp(-dt * 1.6);
    this.lean += this.vel * dt;
    this.bullPitch *= Math.exp(-dt * 4);
    this.bullYaw = Math.sin(this.t * 2.2 * anger) * 0.25;

    if (Math.abs(this.lean) >= 1) {
      this.thrown = true;
      this.overT = this.t;
    } else if (this.t >= RIDE_SECONDS) this.overT = this.t;
  }

  draw(g: CanvasRenderingContext2D, time: number): void {
    const W = this.width;
    const H = this.height;
    const rodeo = this.kind === 'rodeo';
    const bg = g.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, rodeo ? '#1b2340' : '#2a1a14');
    bg.addColorStop(1, rodeo ? '#5a4632' : '#3a2416');
    g.fillStyle = bg;
    g.fillRect(0, 0, W, H);
    // Floodlights or neon.
    for (let i = 0; i < 5; i++) {
      g.fillStyle = rodeo ? 'rgba(255,240,190,0.18)' : `hsla(${(i * 70 + time * 40) % 360},90%,60%,0.25)`;
      g.beginPath();
      g.arc(30 + i * 75, 18, 12, 0, Math.PI * 2);
      g.fill();
    }
    // Ground (dirt or the inflatable pad).
    g.fillStyle = rodeo ? '#7a5a3a' : '#c62828';
    g.fillRect(0, 190, W, 50);
    if (!rodeo) {
      g.fillStyle = '#ffd400';
      g.fillRect(0, 190, W, 5);
    }

    // The bull, pitching and spinning under you.
    g.save();
    g.translate(W / 2, 168);
    g.rotate(this.bullPitch + Math.sin(time * 9) * 0.05 * (this.over ? 0 : 1));
    g.scale(1 - Math.abs(this.bullYaw) * 0.4, 1);
    g.fillStyle = rodeo ? '#3b2a20' : '#6a6f74';
    roundRect(g, -70, -32, 140, 50, 18);
    g.fill();
    g.fillRect(-58, 12, 14, 28);
    g.fillRect(44, 12, 14, 28);
    roundRect(g, 54, -46, 44, 34, 10);
    g.fill();
    g.fillStyle = '#e9dcc0';
    g.beginPath();
    g.moveTo(64, -44);
    g.lineTo(54, -62);
    g.lineTo(72, -46);
    g.moveTo(90, -44);
    g.lineTo(104, -60);
    g.lineTo(94, -42);
    g.fill();
    g.restore();

    // You, leaning.
    g.save();
    g.translate(W / 2, 140);
    g.rotate(this.lean * 1.1 + this.bullPitch);
    g.fillStyle = '#2b6cb0';
    g.fillRect(-9, -48, 18, 40);
    g.fillStyle = '#eab08c';
    g.fillRect(-8, -64, 16, 16);
    g.fillStyle = '#7a4b26';
    g.fillRect(-14, -70, 28, 7);
    g.fillRect(-9, -78, 18, 9);
    // Free hand up, cowboy.
    g.fillStyle = '#eab08c';
    g.fillRect(8, -66 - Math.sin(time * 10) * 4, 6, 22);
    g.restore();

    // Balance meter and the clock.
    g.fillStyle = 'rgba(0,0,0,0.45)';
    roundRect(g, 60, 206, 240, 18, 9);
    g.fill();
    g.fillStyle = 'rgba(80,200,90,0.75)';
    g.fillRect(150, 208, 60, 14);
    const mx = 180 + clamp(this.lean, -1, 1) * 116;
    g.fillStyle = Math.abs(this.lean) > 0.7 ? '#ff4b4b' : '#ffd400';
    g.fillRect(mx - 3, 204, 6, 22);
    banner(g, this.time.toFixed(1) + 's', W - 46, 40, this.time >= RIDE_SECONDS ? '#9dff6a' : '#ffffff', 30);
    if (this.over)
      banner(
        g,
        this.thrown ? 'EATIN’ DIRT!' : 'QUALIFIED RIDE!',
        W / 2,
        96,
        this.thrown ? '#ff8a8a' : '#9dff6a',
        40,
      );
  }
}
